import AdSenseBanner from "@/components/AdSenseBanner";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/lib/auth";
import {
  fetchWallet,
  shopCheckout,
  pollPaymentStatus,
  fetchShopConfig,
  validateShopCoupon,
  fileUrl,
} from "@/lib/api";
import { Coins, Trash2, Minus, Plus, ShoppingCart, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, setQty, remove, clear, subtotal } = useShop();
  const { user } = useAuth();
  const nav = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [dcUse, setDcUse] = useState(0);
  const [method, setMethod] = useState("dc_coins");
  const [placing, setPlacing] = useState(false);
  const [paying, setPaying] = useState(null); // {order_number, confirm_token}
  const [done, setDone] = useState(null);
  const [lostdrkcOn, setLostdrkcOn] = useState(false);

  // Discord coupon
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");

  useEffect(() => { 
    fetchShopConfig().then((c) => setLostdrkcOn(!!c.lostdrkc_enabled)).catch(() => {}); 
  }, []);

  const loadWallet = useCallback(() => {
    if (user) fetchWallet().then(setWallet).catch(() => setWallet(null));
  }, [user]);
  useEffect(() => { loadWallet(); }, [loadWallet]);

  const coinValue = wallet?.coin_value || 2;
  const balance = wallet?.dc_coin || 0;

  const couponDiscount = Number(appliedCoupon?.discount_inr || 0);

  const eligibleTotal = useMemo(
    () => items
      .filter((i) => !i.disable_dc_coin)
      .reduce((s, i) => s + i.price_inr * i.qty, 0),
    [items]
  );

  // The backend calculates this using the real product IDs and
  // DC-coin eligibility. This prevents product-specific coupons
  // from being miscalculated in the browser.
  const couponEligibleTotal = Number(
    appliedCoupon?.dc_eligible_after_coupon ??
    eligibleTotal
  );

  const maxCartCoins = Math.floor(couponEligibleTotal * coinValue);
  const maxAllowed = Math.min(balance, maxCartCoins);
  const clampedUse = Math.max(0, Math.min(dcUse, maxAllowed));
  const discount = +(clampedUse / coinValue).toFixed(2);

  const total = Math.max(
    0,
    +(subtotal - couponDiscount - discount).toFixed(2)
  );

  useEffect(() => { if (dcUse > maxAllowed) setDcUse(maxAllowed); }, [maxAllowed, dcUse]);
  useEffect(() => { if (total <= 0 && clampedUse > 0) setMethod("dc_coins"); }, [total, clampedUse]);
  useEffect(() => { if (total > 0 && method === "dc_coins") setMethod("lostdrkc"); }, [total, method]);

  // poll payment status
  useEffect(() => {
    if (!paying) return;
    const iv = setInterval(async () => {
      try {
        const r = await pollPaymentStatus(paying.order_number, paying.confirm_token);
        if (r.paid) { 
          clearInterval(iv); 
          setPaying(null); 
          setDone(paying.order_number); 
          clear(); 
          toast.success("Payment received! Order confirmed."); 
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [paying, clear]);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();

    if (!code) {
      setCouponError("Enter a coupon code.");
      return;
    }

    if (!user) {
      setCouponError("Please sign in before applying a coupon.");
      return;
    }

    if (items.length === 0) {
      setCouponError("Your cart is empty.");
      return;
    }

    setCouponLoading(true);
    setCouponError("");

    try {
      const res = await validateShopCoupon({
        coupon_code: code,
        items: items.map((i) => ({
          slug: i.slug,
          qty: i.qty,
        })),
      });

      const coupon = res?.coupon;

      if (!coupon) {
        throw new Error("Coupon validation failed.");
      }

      setAppliedCoupon(coupon);
      setCouponCode(coupon.code || code);
      setDcUse(0);

      toast.success(
        `Coupon applied! You saved ₹${Number(coupon.discount_inr || 0).toFixed(2)}.`
      );
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(
        e?.response?.data?.detail ||
        e?.message ||
        "Invalid coupon code."
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    setDcUse(0);
    toast.success("Coupon removed.");
  };

  const placeOrder = async () => {
    if (!user) { toast.error("Please sign in to checkout."); return; }
    if (!wallet?.lostdc_id) { toast.error("Link your Minecraft account first (Profile → Minecraft)."); return; }
    if (items.length === 0) return;
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        dc_to_use: clampedUse,
        payment_method: total <= 0
          ? (clampedUse > 0 ? "dc_coins" : "coupon")
          : method,
        coupon_code: appliedCoupon?.code || "",
      };
      const res = await shopCheckout(payload);
      if (res.requires_payment && res.payment_link) {
        // Calculate popup dimensions & center on screen
        const width = 500;
        const height = 700;
        const left = Math.max(0, (window.screen.width - width) / 2);
        const top = Math.max(0, (window.screen.height - height) / 2);

        window.open(
          res.payment_link,
          "LostDRKCPayment",
          `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
        );

        setPaying({ order_number: res.order_number, confirm_token: res.confirm_token });
        toast.info("Complete the payment in the pop-up window. This page will update automatically.");
      } else {
        setDone(res.order_number); clear(); toast.success("Order placed successfully!");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setPlacing(false); }
  };

  if (done) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="cart-success">
        <CheckCircle2 size={64} className="mx-auto text-[#FFBF00] mb-4" />
        <h1 className="font-display text-3xl mb-2">Order Confirmed!</h1>
        <p className="text-white/60 mb-2">Order <span className="text-[#FFBF00] font-mono">{done}</span> is confirmed.</p>
        <p className="text-white/60 mb-8">Join <span className="text-white">play.lostdrkc.in</span> and run <code className="text-[#FFBF00]">/lostdcstore</code> to claim your items.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/orders" data-testid="cart-view-orders" className="px-6 py-3 rounded-full bg-[#FFBF00] text-black font-display blocky-shadow-black hover-blocky">My Orders</Link>
          <Link to="/store" className="px-6 py-3 rounded-full glass hover-blocky">Keep shopping</Link>
        </div>
      </div>
    );
  }

  if (paying) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="cart-paying">
        <Loader2 size={56} className="mx-auto text-[#FFBF00] mb-4 animate-spin" />
        <h1 className="font-display text-2xl mb-2">Waiting for payment…</h1>
        <p className="text-white/60">Complete your payment in the pop-up window. We verify with the gateway automatically.</p>
        <p className="text-white/40 text-sm mt-4">Order {paying.order_number}</p>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-cart">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-display text-3xl mb-8 flex items-center gap-3"><ShoppingCart /> Your Cart</h1>
        {items.length === 0 ? (
          <div className="text-center py-20 text-white/50" data-testid="cart-empty">
            Your cart is empty. <Link to="/store" className="text-[#FFBF00]">Browse the store</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((i) => (
                <div key={i.slug} data-testid={`cart-item-${i.slug}`} className="flex items-center gap-4 glass rounded-2xl p-4">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-black/40 shrink-0">
                    {i.image && <img src={fileUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display truncate">{i.name}</div>
                    <div className="text-sm text-white/60">₹{i.price_inr}{!i.disable_dc_coin && ` · ${Math.ceil(i.price_inr * coinValue)} DC`}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i.slug, i.qty - 1)} data-testid={`cart-dec-${i.slug}`} className="w-8 h-8 rounded-full glass grid place-items-center hover:border-[#FFBF00]/50"><Minus size={14} /></button>
                    <span className="w-8 text-center" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
                    <button onClick={() => setQty(i.slug, i.qty + 1)} data-testid={`cart-inc-${i.slug}`} className="w-8 h-8 rounded-full glass grid place-items-center hover:border-[#FFBF00]/50"><Plus size={14} /></button>
                  </div>
                  <div className="w-20 text-right font-display">₹{i.price_inr * i.qty}</div>
                  <button onClick={() => remove(i.slug)} data-testid={`cart-remove-${i.slug}`} className="text-white/40 hover:text-red-400"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>

            <div className="glass rounded-2xl p-6 h-fit" data-testid="cart-summary">
              <h3 className="font-display text-xl mb-4">Order Summary</h3>
              {!user ? (
                <p className="text-white/60 text-sm mb-4">Please <Link to="/store" className="text-[#FFBF00]">sign in</Link> to checkout.</p>
              ) : !wallet?.lostdc_id ? (
                <p className="text-yellow-400/90 text-sm mb-4">Link your Minecraft account in <Link to="/profile" className="underline">Profile → Minecraft</Link> to buy.</p>
              ) : (
                <div className="mb-4 p-3 rounded-xl bg-[#FFBF00]/10 border border-[#FFBF00]/25 flex items-center gap-2 text-sm">
                  <Coins size={16} className="text-[#FFBF00]" />
                  <span>Balance: <strong className="text-[#FFBF00]" data-testid="cart-balance">{balance} DC</strong></span>
                </div>
              )}

              <div className="flex justify-between text-sm mb-2"><span className="text-white/60">Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>

              {/* Discord Coupon */}
              {user && (
                <div className="my-4 p-4 rounded-xl border border-white/10 bg-black/30">
                  <div className="text-sm text-white/80 mb-2">
                    Discord Coupon
                  </div>

                  {!appliedCoupon ? (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value.toUpperCase());
                            setCouponError("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") applyCoupon();
                          }}
                          placeholder="Enter coupon code"
                          maxLength={64}
                          disabled={couponLoading}
                          data-testid="cart-coupon-input"
                          className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#FFBF00]/50 focus:outline-none uppercase"
                        />

                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          data-testid="cart-coupon-apply"
                          className="px-4 py-2 rounded-lg bg-[#FFBF00] text-black font-semibold disabled:opacity-50"
                        >
                          {couponLoading ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            "Apply"
                          )}
                        </button>
                      </div>

                      {couponError && (
                        <div
                          className="text-xs text-red-400 mt-2"
                          data-testid="cart-coupon-error"
                        >
                          {couponError}
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20"
                      data-testid="cart-coupon-applied"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-green-400 font-semibold">
                          {appliedCoupon.code}
                        </div>
                        <div className="text-xs text-white/50">
                          Coupon discount: −₹{couponDiscount.toFixed(2)}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={removeCoupon}
                        className="text-xs text-red-400 hover:text-red-300 shrink-0"
                        data-testid="cart-coupon-remove"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              )}

              {appliedCoupon && couponDiscount > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/60">
                    Coupon ({appliedCoupon.code})
                  </span>
                  <span className="text-green-400">
                    −₹{couponDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              {user && wallet && eligibleTotal > 0 && (
                <div className="my-4 p-4 rounded-xl border border-[#FFBF00]/20 bg-black/30">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-white/80">Use DC Coins</label>
                    <button onClick={() => setDcUse(maxAllowed)} data-testid="cart-use-max" className="text-xs px-3 py-1 rounded-full bg-[#FFBF00] text-black font-semibold">⚡ Max</button>
                  </div>
                  <input type="number" min={0} max={maxAllowed} value={clampedUse} data-testid="cart-dc-input"
                    onChange={(e) => setDcUse(parseInt(e.target.value || "0", 10))}
                    className="w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#FFBF00]/50 focus:outline-none" />
                  <div className="text-xs text-white/50 mt-1">
                    Max usable: {maxAllowed} DC · {coinValue} DC = ₹1
                    {appliedCoupon && " · Coupon applied first"}
                  </div>
                  <div className="text-sm text-[#FFBF00] mt-2" data-testid="cart-discount">Discount: −₹{discount.toFixed(2)}</div>
                </div>
              )}

              <div className="flex justify-between font-display text-2xl my-4 pt-4 border-t border-white/10">
                <span>Total</span><span data-testid="cart-total">₹{total.toFixed(2)}</span>
              </div>

              {total > 0 && (
                <div className="mb-4 space-y-2" data-testid="cart-payment-methods">
                  <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Pay remaining with</div>
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#FFBF00] bg-[#FFBF00]/10 cursor-pointer">
                    <input type="radio" name="pm" value="lostdrkc" checked={method === "lostdrkc"} onChange={() => setMethod("lostdrkc")} data-testid="cart-pm-lostdrkc" />
                    <span className="text-sm">LostDRKC Payments</span>
                  </label>
                </div>
              )}

              <button onClick={placeOrder} disabled={placing || !user || !wallet?.lostdc_id} data-testid="cart-place-order"
                className="w-full py-3.5 rounded-full bg-[#FFBF00] text-black font-display text-lg blocky-shadow-black hover-blocky disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
                {placing && <Loader2 size={18} className="animate-spin" />}
                {total <= 0 ? "Pay with DC Coins" : "Place Order"}
              </button>
              <button onClick={clear} className="w-full mt-3 text-xs text-white/40 hover:text-white/70">Clear cart</button>
            </div>
          </div>
        )}

        {/* Bottom AdSense Banner */}
        <div className="mt-12">
          <AdSenseBanner />
        </div>
      </div>
    </div>
  );
}
